import { PATH_PROJECTS } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType, mergeFilters } from "@/lib/filters";
import { FilterEntry } from "@/lib/filters/types";
import { ReadProject } from "@/types/project";
import { ComponentProps, forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option, type AsyncSelectBaseProps } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { EntityListLink } from "./EntityListLink";
import { ProjectService } from "@/modules/projects/service/project";
import { ProjectDependsOn } from "@/schemas/commonFields";
import { NameWithSuffix } from "./NameWithRef";

interface ProjectSelectProps extends AsyncSelectBaseProps {
  className?: string;
  inDialog?: boolean;
  clear?: boolean;
  showLink?: boolean;
  placeholder?: string;
  projectDependsOn?: ProjectDependsOn;
  filters?: FilterEntry[];
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, ProjectSelectProps>(
  function ProjectSelect(
    {
      value,
      inDialog,
      showLink = true,
      projectDependsOn,
      filters: filtersProp,
      defaultItem,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation();
    const name = "projectSelect";
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<ReadProject>(name, options, {
      projectDependsOn,
    });

    const renderProjectNameWithId = ({
      name,
      ref_number,
    }: ReadProject & Option) => (
      <NameWithSuffix name={name} suffix={ref_number} />
    );

    const filters = useMemo(() => {
      const extraFilters = [];

      if (projectDependsOn !== undefined) {
        extraFilters.push({
          id: "project_depends_on",
          value: {
            value: projectDependsOn,
            filter: FilterType.EqualsString,
          },
        });
      }

      if (filtersProp) {
        extraFilters.push(...filtersProp);
      }

      return mergeFilters(options.filters, extraFilters);
    }, [options.filters, projectDependsOn, filtersProp]);
    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            ProjectService.getList({
              filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("projectSelect.placeholder")}
          loadingLabel={t("projectSelect.loadingProjects")}
          noResultsLabel={t("projectSelect.noResults")}
          searchLabel={t("projectSelect.search")}
          enableFilter
          {...props}
          onFilter={onFilter}
          renderName={renderProjectNameWithId}
          value={value ? value.toString() : undefined}
          ref={ref}
          inDialog={inDialog}
          defaultItem={defaultItem}
        />
        {showLink && (
          <EntityListLink
            path={PATH_PROJECTS}
            label={t("projectSelect.link")}
          />
        )}
      </span>
    );
  },
);
